import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box } from '@mui/material';
import useMarketingFonts from '../marketing/useMarketingFonts';
import useSeoHead from '../marketing/useSeoHead';
import MarketingNav from '../marketing/components/MarketingNav';
import MarketingFooter from '../marketing/components/MarketingFooter';
import Eyebrow from '../marketing/components/Eyebrow';
import { marketingColors as C, marketingFonts as F } from '../theme_landing_page';

type Cell = 'yes' | 'no' | 'part';

interface Row {
  label: string;
  landvale: Cell;
  spreadsheets: Cell;
  generic: Cell;
}

const rows: Row[] = [
  { label: 'AI certificate reading', landvale: 'yes', spreadsheets: 'no', generic: 'no' },
  { label: 'Automatic expiry reminders', landvale: 'yes', spreadsheets: 'part', generic: 'part' },
  { label: 'Live portfolio compliance score', landvale: 'yes', spreadsheets: 'no', generic: 'no' },
  { label: 'Open Banking rent tracking', landvale: 'yes', spreadsheets: 'no', generic: 'part' },
  { label: 'AI expense & invoice capture', landvale: 'yes', spreadsheets: 'no', generic: 'part' },
  { label: 'HMO & fire workflows', landvale: 'yes', spreadsheets: 'no', generic: 'no' },
  { label: 'Scoped accountant access', landvale: 'yes', spreadsheets: 'no', generic: 'part' },
  { label: 'Audit-ready evidence export', landvale: 'yes', spreadsheets: 'part', generic: 'no' },
];

const CheckIcon: React.FC = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round">
    <path d="m5 12 5 5L20 7" />
  </svg>
);
const CrossIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.crossIcon} strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);
const PartialLabel: React.FC = () => <Box component="span" sx={{ color: C.partialLabel, fontSize: 12, fontWeight: 600 }}>Partial</Box>;

const ResultCell: React.FC<{ value: Cell; highlighted?: boolean }> = ({ value, highlighted }) => (
  <Box sx={{ padding: { xs: '14px 8px', md: '20px 16px' }, display: 'flex', justifyContent: 'center', alignItems: 'center', background: highlighted ? 'rgba(0,84,249,0.04)' : 'transparent' }}>
    {value === 'yes' && <CheckIcon />}
    {value === 'no' && <CrossIcon />}
    {value === 'part' && <PartialLabel />}
  </Box>
);

const ComparePage: React.FC = () => {
  useMarketingFonts();
  useSeoHead({
    title: 'MillOS vs Spreadsheets vs Generic Tools | Compare',
    description: 'See how MillOS compares to spreadsheets and generic property tools on certificate AI, expiry reminders, portfolio scoring, rent tracking and expense capture.',
    canonicalPath: '/compare',
  });

  return (
    <Box className="lv-marketing" sx={{ background: C.white, color: C.ink, fontFamily: F.body, fontSize: 16, lineHeight: 1.5, overflowX: 'hidden' }}>
      <MarketingNav />

      <Box component="section" sx={{ padding: { xs: '120px 24px 8px', md: '140px 52px 8px' } }}>
        <Box sx={{ maxWidth: 1000, margin: '0 auto', textAlign: 'center' }}>
          <Eyebrow>Compare</Eyebrow>
          <Box component="h1" sx={{ fontFamily: F.display, fontWeight: 400, fontSize: 'clamp(2.5rem,5vw,4.5rem)', lineHeight: 1.1, letterSpacing: '-0.04em', marginTop: '24px' }}>
            Why landlords <Box component="em" sx={{ fontStyle: 'italic' }}>switch</Box>
          </Box>
          <Box sx={{ maxWidth: '38rem', margin: '18px auto 0', fontSize: 17, color: C.body }}>
            Spreadsheets and generic tools weren't built for property compliance. MillOS is.
          </Box>
        </Box>
      </Box>

      <Box component="section" sx={{ padding: { xs: '48px 16px 64px', md: '56px 52px 96px' } }}>
        <Box sx={{ maxWidth: 1000, margin: '0 auto', border: `1px solid ${C.hairline}`, borderRadius: '24px', overflow: 'hidden', overflowX: 'auto' }}>
          <Box sx={{ minWidth: 560 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr 1fr', background: C.ink, color: C.white }}>
              <Box sx={{ padding: { xs: '16px 14px', md: '22px 24px' }, fontSize: 14, fontWeight: 500 }}>Capability</Box>
              <Box sx={{ padding: { xs: '16px 8px', md: '22px 16px' }, fontSize: 14, fontWeight: 600, textAlign: 'center', background: C.accent }}>MillOS</Box>
              <Box sx={{ padding: { xs: '16px 8px', md: '22px 16px' }, fontSize: 13.5, textAlign: 'center', color: 'rgba(255,255,255,0.75)' }}>Spreadsheets</Box>
              <Box sx={{ padding: { xs: '16px 8px', md: '22px 16px' }, fontSize: 13.5, textAlign: 'center', color: 'rgba(255,255,255,0.75)' }}>Generic tools</Box>
            </Box>
            {rows.map((r, i) => (
              <Box key={r.label} sx={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr 1fr', borderTop: `1px solid ${C.hairline}`, background: i % 2 ? C.zebraStripe : C.white }}>
                <Box sx={{ padding: { xs: '14px 14px', md: '20px 24px' }, fontSize: 14.5, color: C.ink }}>{r.label}</Box>
                <ResultCell value={r.landvale} highlighted />
                <ResultCell value={r.spreadsheets} />
                <ResultCell value={r.generic} />
              </Box>
            ))}
          </Box>
        </Box>
        <Box sx={{ maxWidth: 1000, margin: '40px auto 0', textAlign: 'center' }}>
          <Box
            component={RouterLink}
            to="/register"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: C.inkSoft,
              color: C.white,
              borderRadius: '32px',
              padding: '13px 26px',
              fontSize: 16,
              '&:hover': { background: C.accent },
            }}
          >
            Switch to MillOS free
          </Box>
        </Box>
      </Box>

      <MarketingFooter />
    </Box>
  );
};

export default ComparePage;
